import React, { useState, useMemo, memo, useEffect } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Chip,
  Link,
  useMediaQuery,
  CircularProgress,
  Divider,
  Backdrop,
} from "@mui/material";
import { ExpandMore, Image } from "@mui/icons-material";
import { useTheme } from "@mui/system";
import {
  CardContainer,
  formatPrice,
  FullWidthCard,
  SmallButton,
  KeyValueRow,
} from "../../../rcAutoDetails2/tabs/preventivi";
import Dettagli from "../../lib/Dettagli";
import { API_BASE_URL } from "../../../../../config";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { showSnackbar } from "../../../../../redux/slices/snackbar";
import { useFormRCAutoDetails } from "../../rcautoContext";
import ConfermaSalva from "../../lib/ConfermaSalva";
import VaiAiDettagli from "../../lib/VaiAiDettagli";
import CVTQuickQuote from "./CVTQuickQuote";
import {
  RecuperoTargaDialog,
  useRecuperoTarga,
  ESITO_RECUPERO_TARGA,
} from "../../lib/RecuperoTarga";

// il form di riquotazione si mostra solo arrivando dalla lista CVT
const CVT_LIST_PATH = "/pages/cvt";

// alcuni campi del veicolo arrivano come { value, label } invece che come stringa
const plainValue = (field) =>
  field && typeof field === "object"
    ? field.value ?? field.label ?? ""
    : field ?? "";

const CVT = ({ rcAutoGetDetails, handleSave }) => {
  const { formState, dispatch: dispatchForm } = useFormRCAutoDetails();
  const dataset = formState.preventivi?.CVT || [];
  const [expandedItem, setExpandedItem] = useState(null);
  const [contentType, setContentType] = useState(null);
  const [vaiSection, setVaiSection] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isQuoteLoading, setIsQuoteLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { idTarga } = useParams();
  const dispatchSnackbar = useDispatch();

  const handleQuoteSnackbar = (message, severity) => {
    dispatchSnackbar(showSnackbar({ message, severity }));
  };

  const cliente = formState.cliente || {};

  const calculateQuote = async (quoteData) => {
    const token = sessionStorage.getItem("token");
    setIsQuoteLoading(true);
    try {
      // pratica aperta: cliente e veicolo li ha già l'API, si mandano solo
      // i campi del form
      const payload = {
        data: {
          // il parametro `idTarga` dell'URL è la idSubmission della pratica
          idUid: idTarga,
          provincia: quoteData.province,
          provinceCode: quoteData.provinceCode,
          vehicleValue: parseFloat(quoteData.vehicleValue),
          duration: parseInt(quoteData.duration, 10),
          coverageLimit: parseInt(quoteData.gapLimit, 10),
        },
      };

      const response = await fetch(`${API_BASE_URL}/api/dealerQuotation`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: "Bearer " + token,
        },
        body: JSON.stringify(payload),
      });

      const responseData = await response.json();

      if (responseData.status === -1) {
        handleQuoteSnackbar(responseData.message, "error");
        sessionStorage.clear();
        navigate("/auth/sign-in");
        return;
      }
      if (responseData.status === 0) {
        handleQuoteSnackbar(responseData.message, "error");
        return;
      }
      if (responseData.status === 1) {
        const idSubmission = responseData.data.idSubmission;
        if (idSubmission && idSubmission !== idTarga) {
          // la quotazione genera una nuova pratica: si apre il suo dettaglio
          // mantenendo la provenienza, così il form resta disponibile
          navigate(`/pages/rcAutoDetails/${idSubmission}`, {
            state: { fromPath: CVT_LIST_PATH },
          });
        } else {
          await rcAutoGetDetails?.();
        }
      }
    } catch (error) {
      console.error("Errore durante il calcolo del preventivo:", error);
      handleQuoteSnackbar("Errore durante il calcolo del preventivo", "error");
    } finally {
      setIsQuoteLoading(false);
    }
  };

  const quickQuote = (
    <>
      {/* overlay su tutta la pagina durante il ricalcolo del preventivo */}
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isQuoteLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <CVTQuickQuote
        onCalculate={calculateQuote}
        loading={isQuoteLoading}
        defaultProvinceCode={plainValue(cliente.slResProvincia)}
      />
    </>
  );

  const onDetails = useMemo(
    () => dataset?.filter((el) => el.onDetails === 1),
    [dataset],
  );

  useEffect(() => {
    if (onDetails.length > 0 && !vaiSection) {
      setSelectedItem(onDetails[0]);
      setVaiSection(true);
      // Clear onDetails flag in the global state so it doesn't auto-open again on tab switches
      dispatchForm({
        type: "CLEAR_ON_DETAILS",
        payload: { typePrev: "CVT" },
      });
    }
  }, [onDetails, vaiSection, dispatchForm]);

  const handleExpand = (id, type) => {
    if (expandedItem === id && contentType === type) {
      setExpandedItem(null);
      setContentType(null);
    } else {
      setExpandedItem(id);
      setContentType(type);
    }
  };

  const handleVAI = (item) => {
    setSelectedItem(item);
    setVaiSection(true);
  };

  const srDataset = useMemo(
    () => dataset.filter((el) => el?.sR === 1),
    [dataset],
  );
  const otherDataset = useMemo(
    () => dataset.filter((el) => el?.sR === 0),
    [dataset],
  );

  if (vaiSection)
    return (
      <VaiAiDettagli
        setVaiSection={(val) => {
          setVaiSection(val);
          if (!val) {
            setSelectedItem(null);
            dispatchForm({
              type: "CLEAR_ON_DETAILS",
              payload: { typePrev: "CVT" },
            });
          }
        }}
        from="CVT"
        dataset={selectedItem ? [selectedItem] : []}
      />
    );

  if (dataset.length === 0)
    return (
      <Grid container direction="column">
        {quickQuote}
        <Card>
          <CardContent>
            <Typography variant="h5" textAlign="center">
              Nessun preventivo disponibile
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    );

  return (
    <Grid container direction="column">
      {srDataset.map((item) => (
        <PreventivoCard
          key={item.idUID}
          item={item}
          expandedItem={expandedItem}
          contentType={contentType}
          onExpand={handleExpand}
          onVAI={handleVAI}
          rcAutoGetDetails={rcAutoGetDetails}
          handleSave={handleSave}
        />
      ))}

      {quickQuote}

      <Divider sx={{ mb: 4, mt: 2 }}>Preventivi</Divider>

      {otherDataset.map((item) => (
        <PreventivoCard
          key={item.idUID}
          item={item}
          expandedItem={expandedItem}
          contentType={contentType}
          onExpand={handleExpand}
          rcAutoGetDetails={rcAutoGetDetails}
          onVAI={handleVAI}
          handleSave={handleSave}
        />
      ))}
    </Grid>
  );
};

export default CVT;

const PreventivoCard = ({
  item,
  expandedItem,
  contentType,
  onExpand,
  onVAI,
  rcAutoGetDetails,
  handleSave,
}) => {
  const { formState, dispatch } = useFormRCAutoDetails();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isExpanded = expandedItem === item.idUID;
  const navigate = useNavigate();
  const [openDialogConferma, setOpenDialogConferma] = useState(false);
  const [loading, setLoading] = useState(false);
  const { recuperoTarga, tentativoRecupero, attendiRecuperoTarga } =
    useRecuperoTarga();

  const [selectsForConfermSalvaDialog, setSelectsForConfermSalvaDialog] =
    useState(true);
  //loading state
  const dispatchSnackbar = useDispatch();

  // Snackbar helper
  const handleSnackbar = (message, severity) => {
    dispatchSnackbar(showSnackbar({ message, severity }));
  };

  const handleExpandClick = (type) => onExpand(item.idUID, type);

  const handleRefresh = () => {
    if (loading) {
      return;
    } else {
      setLoading(true);
      rcAutoGetDetails().finally(() => setLoading(false));
    }
  };

  const handleSalva = async (idUID) => {
    //Controllare i contatti , per forza cellulare e email e anche la residenza o sede legale, con via, indirizzo, civico e cap
    const token = sessionStorage.getItem("token");
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/rcAuto/quote/${idUID}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            authorization: "Bearer " + token,
          },
        },
      );

      const responseData = await response.json();

      if (responseData.status === -1) {
        navigate("/auth/sign-in");
      }
      if (responseData.status === 0) {
        handleSnackbar(responseData.message, "error");
        return;
      }
      if (responseData.status === 1) {
        handleSnackbar(responseData.message, "success");
        handleRefresh();
      }
      setOpenDialogConferma(false);
    } catch (error) {
      console.error("Errore durante il salvataggio del preventivo:", error);
    }
  };

  const handleModifica = () => {
    setOpenDialogConferma(false);

    // Auto-add missing contact types if not present as predefinito
    const currentContatti = [...(formState.cliente?.contatti || [])];
    const hasCell = currentContatti.some(
      (c) => c.idTipologia === 3 && c.predefinito === 1,
    );
    const hasEmail = currentContatti.some(
      (c) => c.idTipologia === 1 && c.predefinito === 1,
    );

    let updatedContatti = [...currentContatti];
    let changed = false;

    if (!hasCell) {
      updatedContatti.push({
        idUID: crypto.randomUUID(),
        idTipologia: 3, // Cellulare
        valore: "",
        predefinito: 1,
        isNew: 1,
      });
      changed = true;
    }

    if (!hasEmail) {
      updatedContatti.push({
        idUID: crypto.randomUUID(),
        idTipologia: 1, // Email
        valore: "",
        predefinito: 1,
        isNew: 1,
      });
      changed = true;
    }

    if (changed) {
      dispatch({
        type: "UPDATE_CONTATTI_CLIENTE",
        payload: updatedContatti,
      });
    }

    // Prepare errors for missing fields to show helperText
    const newErrors = { ...(formState.errors.Cliente || {}) };
    const addressFields = [
      "slResIndirizzo",
      "slResCap",
      "slResIndirizzoVia",
      "slResCivico",
    ];

    addressFields.forEach((f) => {
      if (!formState.cliente[f]) {
        newErrors[f] = "Campo obbligatorio";
      }
    });

    // Add errors for empty contacts (including the ones we just added)
    const contactsErrors = { ...(newErrors.contatti || {}) };
    updatedContatti.forEach((c, index) => {
      if (!c.valore) {
        contactsErrors[index] = {
          ...(contactsErrors[index] || {}),
          valore: "Campo obbligatorio",
        };
      }
    });

    if (Object.keys(contactsErrors).length > 0) {
      newErrors.contatti = contactsErrors;
    }

    dispatch({
      type: "set_errors",
      scope: "Cliente",
      payload: newErrors,
    });

    dispatch({ type: "SET_MODIFICA_MODE", payload: true });
    dispatch({ type: "SET_ACTIVE_TAB", payload: "Cliente" });
  };

  const idUidAc = item?.idUidAc;
  const idUidS = item?.idUidS;

  async function handleDownloadFile(idUidAu, nome) {
    const token = sessionStorage.getItem("token");
    try {
      const requestOptions = {
        method: "GET",
        headers: {
          authorization: "Bearer " + token,
        },
      };

      const response = await fetch(
        `${API_BASE_URL}/api/pdf/${idUidAu}/${idUidS}/${idUidAc}`,
        requestOptions,
      );
      if (response.status === 200) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = nome;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } else if (response.status === 401) {
        navigate("/auth/sign-in");
      } else {
        handleSnackbar("Errore durante il download del file.", "error");
      }
    } catch (error) {
      console.log(error);
      return error;
    }
  }

  async function handleProcedi(idUidS, targa) {
    const token = sessionStorage.getItem("token");
    try {
      const requestOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: "Bearer " + token,
        },
        body: JSON.stringify({
          idUid: idUidS,
          targa,
          riskValue: "",
          idStato: 2,
        }),
      };

      const response = await fetch(
        `${API_BASE_URL}/api/subMissionUpdate/`,
        requestOptions,
      );

      const responseData = await response.json();
      if (responseData.status === 1) {
        handleSnackbar(responseData.message, "success");
        setOpenDialogConferma(false);
        // il salvataggio e' andato a buon fine: si attende che i dati della
        // targa vengano recuperati lato server
        const esito = await attendiRecuperoTarga(idUidS);
        if (esito === ESITO_RECUPERO_TARGA.PRONTO) {
          handleRefresh();
        } else if (esito === ESITO_RECUPERO_TARGA.ESAURITO) {
          handleSnackbar(
            "I dati della targa non sono ancora disponibili. Aggiorna la pagina tra qualche istante.",
            "warning",
          );
        }
      } else if (responseData.status === 0) {
        handleSnackbar(responseData.message, "error");
      } else if (responseData.status === -1) {
        navigate("/auth/sign-in");
      }
    } catch (error) {
      console.log(error);
      return error;
    }
  }

  return (
    <>
      <RecuperoTargaDialog open={recuperoTarga} tentativo={tentativoRecupero} />

      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            backgroundColor: "cornflower",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Grid item>
          <CardContainer
            color={item?.sCol}
            isExpanded={isExpanded}
            mb={4}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: isMobile && "center",
              /* borderLeft: `8px solid #${item?.sCol}`, */
            }}
          >
            {item?.sR === 1 && (
              <Chip
                sx={{
                  backgroundColor: `#${item?.sCol}`,
                  color: item?.sCol === "336699" ? "white" : "black",
                  fontSize: 16,
                  width: "100%",
                }}
                label={item?.sDesc}
              />
            )}
            <FullWidthCard>
              {/* Left Section */}
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                pt={2}
                sx={{
                  minWidth: "220px",
                  flexBasis: isMobile ? "100%" : "15%",
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  textAlign="center"
                  mb={1}
                  px={1}
                >
                  {item.compagnia}
                </Typography>

                {item.image ? (
                  <img
                    src={`/static/img/companies/${item.image}`}
                    alt={item.Compagnia}
                    style={{
                      width: "110px",
                      height: "110px",
                      objectFit: "contain",
                    }}
                  />
                ) : (
                  <Image
                    alt={item.Compagnia}
                    src={item.logo || ""}
                    sx={{
                      width: 80,
                      height: 80,
                      bgcolor: "gray",
                      marginBottom: 1,
                    }}
                  />
                )}
              </Box>

              {/* Center Section */}
              <CardContent
                sx={{
                  flexGrow: 1,
                  padding: 2,
                  flexBasis: isMobile ? "100%" : "70%",
                }}
              >
                {Object.entries(item.info)
                  .filter(([_, v]) => v !== "")
                  .map(([key, value], id) => (
                    <KeyValueRow key={id} isEven={id % 2 === 0}>
                      <Typography
                        variant="body2"
                        color="textPrimary"
                        fontWeight="bold"
                        textAlign="right"
                      >
                        {key}:
                      </Typography>

                      {key === "Guida" ? (
                        <Chip
                          label={value}
                          color={value === "Libera" ? "success" : "warning"}
                          sx={{
                            ...(value === "Libera" && {
                              backgroundColor: "#2e7d32",
                            }),
                            color: "white",
                            height: 20,
                            fontWeight: "bold",
                          }}
                        />
                      ) : key === "Emissione" ? (
                        <Chip
                          label={value}
                          color={
                            value === "Emissione immediata"
                              ? "success"
                              : value === "Richiesta documenti"
                              ? "warning"
                              : "error"
                          }
                          sx={{
                            ...(value === "Emissione immediata" && {
                              backgroundColor: "#2e7d32",
                            }),
                            color: "white",
                            height: 20,
                            fontWeight: "bold",
                          }}
                        />
                      ) : (
                        <Typography variant="body2" textAlign="right">
                          {value || "-"}
                        </Typography>
                      )}
                    </KeyValueRow>
                  ))}
              </CardContent>

              {/* Right Section */}
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                pt={1}
                sx={{
                  flexBasis: isMobile ? "100%" : "15%",
                  textAlign: "center",
                  minWidth: "220px",
                  justifyContent: "space-between",
                  height: "100px",
                }}
              >
                <Typography
                  variant="h2"
                  fontWeight="bold"
                  color="#FE6A00"
                  mb={4}
                >
                  {formatPrice(item?.dettagli?.prezzoPreventivo)}
                </Typography>
                {item?.sR === 1 ? (
                  <Button
                    sx={{
                      background: item?.sR === 1 ? "#407ad6" : "#5cb85c", // Dettagli colore blu
                      color: "white",
                      width: "80%",
                      fontSize: "1rem",
                      "&:hover": {
                        background:
                          item?.sR === 1 ? "rgb(44, 85, 159)" : "#499149",
                      }, //44, 85, 159
                    }}
                    onClick={() => onVAI(item)} // Richiamare un'api dopo se SR === 0
                  >
                    Dettagli
                  </Button>
                ) : item?.sR === 0 && item?.aQ === 1 ? (
                  <Button
                    sx={{
                      background: item?.sR === 1 ? "#407ad6" : "#5cb85c", // Dettagli colore blu
                      color: "white",
                      width: "80%",
                      fontSize: "1rem",
                      "&:hover": {
                        background:
                          item?.sR === 1 ? "rgb(44, 85, 159)" : "#499149",
                      }, //44, 85, 159
                    }}
                    onClick={() => setOpenDialogConferma(true)}
                  >
                    Salva
                  </Button>
                ) : null}
              </Box>
            </FullWidthCard>

            <ConfermaSalva
              selectForConfermSalvaDialog={selectsForConfermSalvaDialog}
              open={openDialogConferma}
              //loading={loading}
              setOpen={setOpenDialogConferma}
              onConfirm={async (targa) =>
                await handleProcedi(item?.idUidS, targa)
              }
              onModifica={handleModifica}
            />

            {/* Buttons */}
            <Box display="flex" justifyContent="flex-start" p={2}>
              {item?.documenti?.length > 0 ? (
                <SmallButton
                  onClick={() => handleExpandClick("table")}
                  sx={{ mr: 2 }}
                  $isActive={isExpanded && contentType === "table"}
                >
                  <ExpandMore
                    sx={{
                      transform:
                        isExpanded && contentType === "table"
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                      transition: "transform 0.3s",
                    }}
                  />
                  Documenti
                </SmallButton>
              ) : null}

              <SmallButton
                onClick={() => handleExpandClick("list")}
                $isActive={isExpanded && contentType === "list"}
              >
                <ExpandMore
                  sx={{
                    transform:
                      isExpanded && contentType === "list"
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                    transition: "transform 0.3s",
                  }}
                />
                Dettagli
              </SmallButton>
            </Box>

            {/* Expandable Section */}
            {isExpanded && (
              <Box p={2} width="100%">
                {contentType === "table" && item?.documenti?.length > 0 && (
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Nome</TableCell>
                        <TableCell>Tipo</TableCell>
                        <TableCell>Fonte</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {item.documenti.map((doc) => (
                        <TableRow key={doc.idUidDocumento}>
                          <TableCell>
                            <Link
                              sx={{
                                cursor: "pointer",
                                color:
                                  theme.palette.mode === "light"
                                    ? "#0c4bb7"
                                    : "#5c92e1",
                                fontWeight: 900,
                              }}
                              onClick={() =>
                                handleDownloadFile(doc.idUidAu, doc.nome)
                              }
                            >
                              {doc.nome}
                            </Link>
                          </TableCell>
                          <TableCell>{doc.tipo}</TableCell>
                          <TableCell>{doc.fonte}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
                {contentType === "list" && (
                  <Dettagli idUID={item.idUID} type="CVT" />
                )}
              </Box>
            )}
          </CardContainer>
        </Grid>
      )}
    </>
  );
};

memo(PreventivoCard);
